// HotspotLoans landing interactions
import './scss/style.scss';

const LOAN_AMOUNT_URL_PARAM = 'loanAmount';

// ---------------------------------------------------------------------------
// tPar cookie helpers (apply.php)
// Stores attribution + PII as plain JSON; partner reads macros {%tPar_*%}
// and echoes them in postback → track.php hashes PII before Meta CAPI.
// ---------------------------------------------------------------------------
const COOKIE_NAME_TPAR = 'tPar';
const COOKIE_EXPIRES_DAYS = 365;

function getTParCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length !== 2) return '';
  try {
    return decodeURIComponent(parts.pop().split(';').shift().trim());
  } catch (e) {
    return '';
  }
}

function setTParCookie(name, value, days) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
}

function readTPar() {
  const raw = getTParCookie(COOKIE_NAME_TPAR);
  if (!raw) return {};
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch (e) {
    try {
      return JSON.parse(raw);
    } catch (e2) {
      return {};
    }
  }
}

function writeTPar(tPar) {
  setTParCookie(COOKIE_NAME_TPAR, encodeURIComponent(JSON.stringify(tPar)), COOKIE_EXPIRES_DAYS);
}

function updateTParField(key, value) {
  const normalized = value == null ? '' : String(value).trim();
  if (!normalized) return;
  const tPar = readTPar();
  tPar[key] = normalized;
  writeTPar(tPar);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Profitner form fields synced into tPar on apply.php.
 * Keys match postback macros: {%tPar_em%}, {%tPar_fn%}, {%tPar_ln%}.
 */
const PARTNER_TPAR_FIELDS = [
  {
    tparKey: 'em',
    selectors: [
      '#omForm input#email',
      '#omForm input.om-input.om-input_text.omInputText[name="email"]',
      '#omForm input.om-input.om-input_text.omInputText[type="email"]',
      '#omForm input.om-input.omInputText[name="email"]',
      '#omForm input[name="email"]',
      '#omForm input[type="email"]',
    ],
    matchInput(element) {
      const id = (element.id || '').toLowerCase();
      const name = (element.name || '').toLowerCase();
      if (id === 'email' || name === 'email') return true;
      if (element.type === 'email') return true;
      const label = element.closest('.om-input__container')?.querySelector('.om-input__label');
      return Boolean(label && /e-?mail/i.test(label.textContent || ''));
    },
    normalize: (value) => value.trim().toLowerCase(),
    isValid: isValidEmail,
  },
  {
    tparKey: 'fn',
    selectors: [
      '#omForm input#firstName',
      '#omForm input.om-input.om-input_text.omInputText[name="firstName"]',
      '#omForm input[name="firstName"]',
    ],
    matchInput(element) {
      const id = (element.id || '').toLowerCase();
      const name = (element.name || '').toLowerCase();
      if (id === 'firstname' || name === 'firstname') return true;
      const label = element.closest('.om-input__container')?.querySelector('.om-input__label');
      return Boolean(label && /first\s*name/i.test(label.textContent || ''));
    },
    normalize: (value) => value.trim(),
    isValid: (value) => value.length > 0,
  },
  {
    tparKey: 'ln',
    selectors: [
      '#omForm input#lastName',
      '#omForm input.om-input.om-input_text.omInputText[name="lastName"]',
      '#omForm input[name="lastName"]',
    ],
    matchInput(element) {
      const id = (element.id || '').toLowerCase();
      const name = (element.name || '').toLowerCase();
      if (id === 'lastname' || name === 'lastname') return true;
      const label = element.closest('.om-input__container')?.querySelector('.om-input__label');
      return Boolean(label && /last\s*name/i.test(label.textContent || ''));
    },
    normalize: (value) => value.trim(),
    isValid: (value) => value.length > 0,
  },
  {
    tparKey: 'ph',
    selectors: [
      '#omForm input#homePhone',
      '#omForm input[name="homePhone"]',
      '#omForm input[name="phone"]',
      '#omForm input[type="tel"]',
    ],
    matchInput(element) {
      const id = (element.id || '').toLowerCase();
      const name = (element.name || '').toLowerCase();
      if (id.includes('phone') || name.includes('phone')) return true;
      if (element.type === 'tel') return true;
      const label = element.closest('.om-input__container')?.querySelector('.om-input__label');
      return Boolean(label && /phone/i.test(label.textContent || ''));
    },
    // Digits only — the partner form renders (555) 123-4567 style masks.
    normalize: (value) => value.replace(/\D/g, ''),
    isValid: (value) => value.length >= 10,
  },
];

const lastSyncedPartnerFields = {};

function isPartnerOmTextInput(element) {
  return element instanceof HTMLInputElement
    && element.closest('#omForm')
    && element.matches('input.om-input.om-input_text.omInputText, input.om-input.omInputText, input[type="text"], input[type="email"], input[type="tel"]');
}

function getPartnerFieldConfigForInput(element) {
  if (!isPartnerOmTextInput(element)) return null;
  return PARTNER_TPAR_FIELDS.find((field) => field.matchInput(element)) || null;
}

function getPartnerFieldInput(field) {
  for (const selector of field.selectors) {
    const input = document.querySelector(selector);
    if (input instanceof HTMLInputElement) return input;
  }
  return null;
}

function syncPartnerFieldToTPar(field, value) {
  const normalized = field.normalize(String(value || ''));
  if (!field.isValid(normalized)) return;
  if (normalized === lastSyncedPartnerFields[field.tparKey]) return;
  lastSyncedPartnerFields[field.tparKey] = normalized;
  updateTParField(field.tparKey, normalized);
}

function syncPartnerFieldsFromDom() {
  PARTNER_TPAR_FIELDS.forEach((field) => {
    const input = getPartnerFieldInput(field);
    if (input) syncPartnerFieldToTPar(field, input.value);
  });
}

/**
 * Watch Profitner multi-step form (#omForm) and write em/fn/ln to tPar.
 * Uses event delegation + polling because React remounts inputs between steps.
 */
function initPartnerFormFieldsSync() {
  const handleFormEvent = (event) => {
    const field = getPartnerFieldConfigForInput(event.target);
    if (field) syncPartnerFieldToTPar(field, event.target.value);
  };

  document.addEventListener('input', handleFormEvent, true);
  document.addEventListener('change', handleFormEvent, true);
  document.addEventListener('blur', handleFormEvent, true);
  document.addEventListener('keyup', handleFormEvent, true);
  document.addEventListener('paste', handleFormEvent, true);

  document.addEventListener('click', (event) => {
    const navBtn = event.target.closest('.om-navigation__next, .om-navigation__submit, .om-navigation__prev');
    if (!navBtn?.closest('#omForm')) return;
    setTimeout(syncPartnerFieldsFromDom, 0);
    setTimeout(syncPartnerFieldsFromDom, 300);
  }, true);

  const pollFields = () => syncPartnerFieldsFromDom();

  setInterval(pollFields, 800);

  const observer = new MutationObserver(pollFields);
  observer.observe(document.body, { childList: true, subtree: true });

  pollFields();
  [300, 1000, 2000, 5000].forEach((delay) => {
    setTimeout(pollFields, delay);
  });
}

function syncLoanAmountParamToUrl(amount) {
  const loanAmount = amount ? String(amount) : '';
  if (!history.replaceState) return;
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete('loanAmount');
  searchParams.delete('requested_amount');
  if (loanAmount) {
    searchParams.set(LOAN_AMOUNT_URL_PARAM, loanAmount);
  } else {
    searchParams.delete(LOAN_AMOUNT_URL_PARAM);
  }
  const qs = searchParams.toString();
  const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  history.replaceState({ path: newUrl }, '', newUrl);
}

function getPartnerLoanAmountSelect() {
  return document.querySelector('#omForm select#loanAmount, #omForm select[name="loanAmount"]');
}

function bindPartnerLoanAmountSelect(select) {
  if (select.dataset.loanAmountSyncBound === '1') return;
  select.dataset.loanAmountSyncBound = '1';

  const syncFromSelect = () => {
    syncLoanAmountParamToUrl((select.value || '').trim());
  };

  const prefillFromUrl = () => {
    if ((select.value || '').trim()) return;
    const urlAmount = new URLSearchParams(window.location.search).get(LOAN_AMOUNT_URL_PARAM)
      || new URLSearchParams(window.location.search).get('requested_amount');
    if (!urlAmount) return;
    const option = Array.from(select.options).find((opt) => opt.value === urlAmount && !opt.disabled);
    if (option) {
      select.value = urlAmount;
    }
  };

  prefillFromUrl();
  syncFromSelect();
  select.addEventListener('change', syncFromSelect);
  [300, 1000, 2000].forEach((delay) => {
    setTimeout(syncFromSelect, delay);
  });
}

function initPartnerLoanAmountSync() {
  const tryBind = () => {
    const select = getPartnerLoanAmountSelect();
    if (!select) return false;
    bindPartnerLoanAmountSelect(select);
    return true;
  };

  if (tryBind()) return;

  const observer = new MutationObserver(() => {
    if (tryBind()) observer.disconnect();
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function getLoanAmountForTracking() {
  const fromSelect = getPartnerLoanAmountSelect()?.value?.trim();
  if (fromSelect) return fromSelect;
  return new URLSearchParams(window.location.search).get(LOAN_AMOUNT_URL_PARAM)
    || new URLSearchParams(window.location.search).get('requested_amount')
    || '';
}

// ---------------------------------------------------------------------------
// Tracking layer
//
// Events go to dataLayer under the names agreed with Stape; GTM forwards them
// to the server container. Direct fbq calls keep their CURRENT names so existing
// Meta custom conversions do not break — they are retired, or given a shared
// event_id for deduplication, when the server-side tags go live.
// ---------------------------------------------------------------------------

// Attribution params that must ride along with every event.
// GA4 client_id lives in tPar under the legacy key `firebase`.
const COMMON_PARAM_SOURCES = {
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbc: 'fbc',
  fbp: 'fbp',
  client_id: 'firebase',
};

// Reset on every push: GTM dataLayer variables persist across pushes, so a
// button_name from one event would otherwise leak into the next.
const EVENT_SPECIFIC_KEYS = ['button_name', 'leadId', 'email', 'phone', 'first_name', 'last_name'];

function getCommonTrackingParams() {
  const tPar = readTPar();
  const params = {};
  Object.keys(COMMON_PARAM_SOURCES).forEach((target) => {
    const value = tPar[COMMON_PARAM_SOURCES[target]];
    if (value) params[target] = String(value);
  });

  // tPar is only written ~2s after load, so events firing on DOMContentLoaded
  // (InitiateCheckout) would ship without attribution on a first visit. Read the
  // original sources as a fallback — they are available immediately.
  const fromUrl = new URLSearchParams(window.location.search);
  ['gclid', 'gbraid', 'wbraid'].forEach((key) => {
    if (!params[key] && fromUrl.get(key)) params[key] = fromUrl.get(key);
  });
  if (!params.fbp) {
    const fbp = getTParCookie('_fbp');
    if (fbp) params.fbp = fbp;
  }
  if (!params.fbc) {
    const fbc = getTParCookie('_fbc');
    if (fbc) params.fbc = fbc;
  }
  if (!params.client_id) {
    const ga = getTParCookie('_ga');
    if (ga) params.client_id = ga.substring(6);
  }

  return params;
}

function pushDataLayer(eventName, params) {
  const payload = { event: eventName };
  EVENT_SPECIFIC_KEYS.forEach((key) => {
    payload[key] = undefined;
  });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(Object.assign(payload, getCommonTrackingParams(), params || {}));
}

function trackMeta(eventName, params) {
  if (window.hotspotMetaPixel && typeof window.hotspotMetaPixel.track === 'function') {
    window.hotspotMetaPixel.track(eventName, params);
    return;
  }
  if (typeof fbq === 'function') {
    fbq('track', eventName, params);
  }
}

/** Lead fields synced from the partner form, keyed as the Stape spec expects. */
function getLeadTrackingParams() {
  const tPar = readTPar();
  const params = {};
  if (tPar.em) params.email = tPar.em;
  if (tPar.ph) params.phone = tPar.ph;
  if (tPar.fn) params.first_name = tPar.fn;
  if (tPar.ln) params.last_name = tPar.ln;
  if (tPar.leadId) params.leadId = tPar.leadId;
  return params;
}

/** CTA on the landing page. button_name is top | main | bottom. */
function trackClickButton(buttonName) {
  pushDataLayer('ClickButton', { button_name: buttonName || '' });
}

/** FAQ accordion — not part of the Stape spec, Meta-only as before. */
function trackClickButton1(contentName = 'FAQ Accordion') {
  trackMeta('ClickButton1', { content_name: contentName });
}

/** CTA inside the pop-up that leads to the application form. */
function trackStartApplication() {
  pushDataLayer('StartApplication', {});
  trackMeta('ClickButton2', { content_name: 'Apply Modal Continue' });
}

function trackViewContent(contentName = 'Apply Modal') {
  const params = { content_name: contentName };
  pushDataLayer('ViewContent', params);
  trackMeta('ViewContent', params);
}

function getApplyFormTrackingParams() {
  const params = { content_name: 'Apply Form', currency: 'USD' };
  const requestedAmount = getLoanAmountForTracking();
  if (requestedAmount) {
    params.loanAmount = requestedAmount;
    const value = parseFloat(requestedAmount);
    if (!Number.isNaN(value)) {
      params.value = value;
    }
  }
  return params;
}

function trackInitiateCheckout() {
  const params = getApplyFormTrackingParams();
  pushDataLayer('InitiateCheckout', params);
  trackMeta('InitiateCheckout', params);
}

function trackSubmitApplication() {
  const params = getApplyFormTrackingParams();
  pushDataLayer('SubmitApplication', Object.assign({}, params, getLeadTrackingParams()));
  trackMeta('SubmitApplication', params);
}

document.addEventListener('DOMContentLoaded', () => {
  const applyPage = document.querySelector('.landing-page--apply');
  if (applyPage) {
    trackInitiateCheckout();
    initPartnerLoanAmountSync();
    initPartnerFormFieldsSync();
    document.addEventListener('click', (event) => {
      const submitBtn = event.target.closest('.om-navigation__submit');
      if (!submitBtn || !applyPage.contains(submitBtn)) return;
      syncPartnerFieldsFromDom();
      trackSubmitApplication();
    });
  }

  document.querySelectorAll('.footer__copyright-year').forEach((el) => {
    const startYear = Number(el.dataset.startYear) || 2019;
    const currentYear = new Date().getFullYear();
    el.textContent = startYear === currentYear ? String(startYear) : `${startYear}–${currentYear}`;
  });

  const header = document.querySelector('.site-header');

  document.querySelectorAll('.site-header__nav-link').forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      const offset = header instanceof HTMLElement ? header.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - offset - 8;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    });
  });

  const applyModal = document.getElementById('applyModal');
  const applyModalContinue = document.getElementById('applyModalContinue');
  const applyModalTriggers = document.querySelectorAll('.js-apply-modal-trigger');
  const landingPage = document.querySelector('.landing-page--home');
  let applyModalLastFocus = null;

  const getHeroApplyLink = () => document.querySelector('.hero__calculator a[href*="apply.php"]');
  const getApplyLinks = () => document.querySelectorAll('.js-apply-modal-trigger, #applyModalContinue, a[href*="apply.php"]');

  const setApplyLinkHref = (link, loanAmount) => {
    const href = (link.getAttribute('href') || '').trim();
    if (!href.includes('apply.php')) return;
    try {
      const url = new URL(href, window.location.href);
      if (loanAmount) {
        url.searchParams.set(LOAN_AMOUNT_URL_PARAM, loanAmount);
        url.searchParams.delete('requested_amount');
      } else {
        url.searchParams.delete(LOAN_AMOUNT_URL_PARAM);
        url.searchParams.delete('requested_amount');
      }
      link.setAttribute('href', `${url.pathname}${url.search}`);
    } catch (e) {
      // ignore invalid href
    }
  };

  const updateApplyLinksWithLoanAmount = (amount) => {
    const loanAmount = amount ? String(amount) : '';
    syncLoanAmountParamToUrl(loanAmount);
    const heroLink = getHeroApplyLink();
    if (heroLink) {
      setApplyLinkHref(heroLink, loanAmount);
    }
    getApplyLinks().forEach((link) => {
      if (link.closest('.hero__calculator')) return;
      setApplyLinkHref(link, '');
    });
  };

  const amountSelect = document.getElementById('loanAmountSelect');

  const syncModalContinueHref = (trigger) => {
    if (!applyModalContinue) return;
    const includeLoanAmount = Boolean(trigger?.closest('.hero__calculator'));
    const loanAmount = includeLoanAmount && amountSelect?.value ? String(amountSelect.value) : '';
    setApplyLinkHref(applyModalContinue, loanAmount);
  };

  const openApplyModal = (trigger) => {
    syncModalContinueHref(trigger);
    if (!applyModal) return;
    applyModalLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    applyModal.classList.add('is-open');
    applyModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('apply-modal-open');
    applyModalContinue?.focus();
    if (landingPage) {
      trackViewContent('Apply Modal');
    }
  };

  const closeApplyModal = () => {
    if (!applyModal) return;
    applyModal.classList.remove('is-open');
    applyModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('apply-modal-open');
    applyModalLastFocus?.focus();
  };

  applyModalTriggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (landingPage) {
        trackClickButton(trigger.dataset.buttonName);
      }
      openApplyModal(trigger);
    });
  });

  applyModalContinue?.addEventListener('click', () => {
    if (landingPage) {
      trackStartApplication();
    }
  });

  applyModal?.querySelectorAll('[data-apply-modal-close]').forEach((element) => {
    element.addEventListener('click', closeApplyModal);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && applyModal?.classList.contains('is-open')) {
      closeApplyModal();
    }
  });

  if (amountSelect) {
    const updateFromSelect = () => {
      const amount = amountSelect.value;
      if (!amount) {
        amountSelect.classList.add('hero__select--placeholder');
        updateApplyLinksWithLoanAmount('');
        return;
      }
      amountSelect.classList.remove('hero__select--placeholder');
      updateApplyLinksWithLoanAmount(amount);
    };

    amountSelect.addEventListener('change', updateFromSelect);
    updateFromSelect();
  } else {
    getApplyLinks().forEach((link) => {
      if (link.closest('.hero__calculator')) return;
      setApplyLinkHref(link, '');
    });
  }

  document.querySelectorAll('.faq__item-header').forEach((button) => {
    button.addEventListener('click', () => {
      const item = button.closest('.faq__item');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');

      document.querySelectorAll('.faq__item').forEach((el) => {
        el.classList.remove('is-open');
        const toggle = el.querySelector('.faq__toggle');
        const btn = el.querySelector('.faq__item-header');
        if (toggle) toggle.textContent = '+';
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        item.classList.add('is-open');
        const toggle = item.querySelector('.faq__toggle');
        if (toggle) toggle.textContent = '−';
        button.setAttribute('aria-expanded', 'true');
        if (landingPage) {
          const title = item.querySelector('.faq__item-title')?.textContent?.trim();
          trackClickButton1(title ? `FAQ: ${title}` : 'FAQ Accordion');
        }
      }
    });
  });
});
